import { useEffect, useState } from 'react';
import { api, voiceLabel, VOICES, type Allowance, type Brand, type Defaults, type TalentRow, type Voice } from '../lib/api';
import { PICTURE_WAIT, LOOK_EVERY_MS, GIVE_UP_AFTER, GAVE_UP, useElapsed, sayWait } from '../lib/waiting';

// Talent (T228, 191): everyone who reads on the platform's shows, in one place.
//
// The vault comes first: a person is a row of the workspace's own — a name,
// a SHEET (what they look like, fixed: "anime boy, twelve, short black hair,
// blue shirt, grey shorts"), a portrait drawn from the sheet, a voice, a bio.
// The sheet and the portrait are what keep a drawn character the same in
// every scene; an AI avatar is talent like anyone else. Casting on a show
// copies voice, face and bio onto the show's kit, where they were always
// kept (062/078/080), so nothing downstream changes.
//
// Below it, the shows: who is cast where, the director's bio draft, and the
// workspace's default reader.
export function Talent({ brands, defaults, onDefaults, onOpenKit, onBrandSaved }: { brands: Brand[]; defaults: Defaults; onDefaults: (d: Defaults) => void; onOpenKit: (brandId: string) => void; onBrandSaved?: (b: Brand) => void }) {
  const [rows, setRows] = useState<TalentRow[]>([]);
  // What is left to spend on pictures this month, so a card can say the price
  // before the press. It is the allowance that is actually enforced; the
  // credit ladder is priced but not yet charged, so quoting credits here
  // would be a number nobody is billed.
  const [images, setImages] = useState<Allowance | null>(null);
  // The library's finished pictures, so a portrait can be CHOSEN rather than
  // bought. A concept sheet filed last month is a better portrait than a
  // redraw of it, and it costs nothing.
  const [pictures, setPictures] = useState<Array<{ id: string; name: string; brand_id: string | null }>>([]);
  const [faces, setFaces] = useState<Record<string, string>>({});
  const [voiceList, setVoiceList] = useState<Voice[]>(VOICES.map((v) => ({ id: v.id, label: v.label, tier: 'local' as const, language: v.id.slice(0, 5).replace('_', '-'), accent: v.hint })));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newSheet, setNewSheet] = useState('');
  // 195: a name already taken is not an error worth a red line — it is almost
  // always somebody meaning to edit that person. So the form takes you to
  // them and says so. The 409 stays underneath as the guarantee.
  const [found, setFound] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; brandId: string; bio: string } | null>(null);
  const [drafting, setDrafting] = useState<string | null>(null);
  const people = gather(brands);

  async function load() {
    try { const r = await api.talent(); setRows(r.talent); setImages(r.images); }
    catch (e) { setError((e as Error).message); }
    try {
      const all = await api.assets();
      setPictures(all.filter((a) => a.kind === 'image' && a.status === 'ready' && a.name)
        .map((a) => ({ id: a.id, name: a.name as string, brand_id: a.brand_id })));
    } catch { /* the picker is a convenience; the vault still works without it */ }
  }
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    let live = true;
    api.voices().then((list) => { if (live && list.length) setVoiceList(list); }).catch(() => {});
    return () => { live = false; };
  }, []);
  // A portrait is not ready the moment it is drawn — the render plane probes
  // and normalises it first, which takes about a minute, and the thumb answers
  // 404 until then. This used to ask once, so a face that was still promoting
  // never arrived and the creator had to reload to find out it had worked.
  // Now it keeps asking, and stops the moment nothing is missing.
  // The faces themselves, once their pictures are ready. A thumb that answers
  // is an answer forever, so each id is fetched once.
  const wantFaces = [...new Set([
    ...rows.filter((r) => r.portrait === 'ready').map((r) => r.portrait_asset_id),
    ...rows.flatMap((r) => r.portrait_takes), ...people.map((p) => p.portraitId),
  ].filter((x): x is string => Boolean(x)))].filter((id) => !faces[id]).join(',');
  useEffect(() => {
    if (!wantFaces) return;
    let live = true;
    for (const id of wantFaces.split(',')) {
      api.assetThumbnail(id).then((u) => { if (live && u) setFaces((m) => ({ ...m, [id]: u })); }).catch(() => undefined);
    }
    return () => { live = false; };
  }, [wantFaces]);

  // 195: the WATCH is now on the talent list, not on a thumbnail, because the
  // server is the one that knows whether a portrait is still coming. That is
  // what makes this survive a reload, a second tab and another machine —
  // page state could not, which is how the same face got bought twice.
  const [stalled, setStalled] = useState(false);
  const [retry, setRetry] = useState(0);
  const inFlight = rows.some((r) => r.portrait === 'drawing' || r.portrait === 'developing');
  useEffect(() => {
    if (!inFlight) { setStalled(false); return; }
    let live = true;
    let looks = 0;
    const t = setInterval(() => {
      // Bounded, and running out is SAID rather than swallowed: a silent stop
      // looks exactly like still working.
      if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); if (live) setStalled(true); return; }
      void load();
    }, LOOK_EVERY_MS);
    return () => { live = false; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight, retry]);
  const lookAgain = () => { setStalled(false); setRetry((n) => n + 1); void load(); };

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key); setError(null); setNote(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  const groups = (() => {
    const out = new Map<string, Voice[]>();
    for (const v of voiceList) {
      const key = v.tier === 'local' ? 'In the render image' : v.tier === 'directed' ? 'Directed — reads the way you tell it' : `${v.accent} · ${v.language}`;
      out.set(key, [...(out.get(key) ?? []), v]);
    }
    return [...out.entries()];
  })();

  // T233: the director drafts a bio from the person's own lines on the show; the creator keeps it or not.
  const draftBio = async (p: Person) => {
    const show = p.shows[0]; if (!show) return;
    setDrafting(p.name); setError(null); setDraft(null);
    try { const r = await api.draftTalentBio(show.id, p.name); setDraft({ name: p.name, brandId: show.id, bio: r.bio }); }
    catch (e) { setError((e as Error).message); } finally { setDrafting(null); }
  };
  const keepBio = async () => {
    if (!draft) return;
    const b = brands.find((x) => x.id === draft.brandId); if (!b) return;
    await run('bio', async () => { const saved = await api.saveBrandKit(b.id, { bios: { ...(b.bios ?? {}), [draft.name]: draft.bio } }); onBrandSaved?.(saved); setDraft(null); });
  };
  /** Take the creator to a person's card and say why they are looking at it. */
  function goTo(who: TalentRow, why: string) {
    setError(null); setNote(why); setFound(who.id); setNewName(''); setNewSheet('');
    requestAnimationFrame(() => document.querySelector(`[data-talent="${who.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    setTimeout(() => setFound((f) => (f === who.id ? null : f)), 4000);
  }

  const setDefault = (name: string | null) => run('default', async () => { const r = await api.saveDefaults({ ...defaults, talent: name }); onDefaults(r.defaults); });

  return (
    <div data-tool="talent" style={{ display: 'grid', gap: 18 }}>
      <div className="row" style={{ alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Talent</h2>
        <span className="sub-dim">Everyone who reads on your shows — a name, what they look like, a face, a voice, a bio. An AI avatar is talent like anyone else.</span>
      </div>
      {error && <p className="err" role="alert">{error}</p>}
      {note && <p className="ok" style={{ margin: 0 }}>{note}</p>}

      {/* The vault: the workspace's own people. */}
      <section style={{ display: 'grid', gap: 10 }} data-tool="talent-vault">
        <form className="row wrap" style={{ gap: 8, alignItems: 'center' }} onSubmit={(e) => {
          e.preventDefault();
          const name = newName.trim(); if (!name) return;
          const already = rows.find((r) => r.name.toLowerCase() === name.toLowerCase());
          if (already) { goTo(already, `${already.name} is already in the vault — this is them.`); return; }
          void run('new', async () => {
            try { await api.addTalent({ name, sheet: newSheet.trim() || null }); }
            catch (e) {
              // Lost the race, or a spelling the client compare missed. The
              // server is the one that decides; follow its answer to the card.
              const mine = (await api.talent()).talent.find((r) => r.name.toLowerCase() === name.toLowerCase());
              if (mine) { goTo(mine, `${mine.name} is already in the vault — this is them.`); return; }
              throw e;
            }
            setNewName(''); setNewSheet(''); await load();
          });
        }}>
          <input value={newName} maxLength={60} placeholder="A name — Niko" onChange={(e) => setNewName(e.target.value)} style={{ width: 160 }} />
          <input value={newSheet} maxLength={600} placeholder="What they look like — anime boy, twelve, short black hair, blue shirt, grey shorts" onChange={(e) => setNewSheet(e.target.value)} style={{ flex: 1, minWidth: 280 }} />
          <button className="btn pri" type="submit" disabled={busy === 'new' || !newName.trim()}>Add talent</button>
        </form>
        {rows.length === 0 && <p className="sub-dim" style={{ margin: 0 }}>Nobody in the vault yet. Add a name and what they look like; the portrait is drawn from that, and every scene they are in keeps to it.</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
          {rows.map((t) => <TalentCard key={t.id} t={t} face={t.portrait_asset_id ? faces[t.portrait_asset_id] : undefined} busy={busy} groups={groups} brands={brands} pictures={pictures}
                                       faces={faces} images={images} stalled={stalled} onLookAgain={lookAgain} found={found === t.id}
                                       onRun={run} onChanged={load} onBrandSaved={onBrandSaved} onNote={setNote} />)}
        </div>
      </section>

      {/* The shows: who is cast where. */}
      <section style={{ display: 'grid', gap: 8 }}>
        <h3 style={{ margin: 0 }}>On the shows</h3>
        {people.length === 0 ? (
          <p className="sub-dim" style={{ margin: 0 }}>Nobody cast yet. Cast someone from the vault above, or open a show's kit: {brands.map((b) => <button key={b.id} type="button" className="btn tiny" style={{ marginLeft: 6 }} onClick={() => onOpenKit(b.id)}>{b.name}</button>)}</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {people.map((p, i) => {
              const isDefault = defaults.talent === p.name;
              const firstFaceless = !p.portraitId && p.voices.length > 0 && people.findIndex((x) => !x.portraitId && x.voices.length > 0) === i;
              return (
                <div key={p.name} className="card" data-tool="talent-card" data-move={firstFaceless ? 'talent-face' : undefined} style={{ display: 'grid', gap: 6, padding: 10, borderColor: isDefault ? 'var(--accent, #e0b100)' : undefined }}>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <Face src={p.portraitId ? faces[p.portraitId] : undefined} name={p.name} />
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: 14 }}>{p.name}</strong>{isDefault && <span className="chip" style={{ marginLeft: 6 }}>default</span>}
                      <div className="sub-dim" style={{ fontSize: 12 }}>{p.voices.length ? p.voices.map((v) => voiceLabel(v, voiceList)).join(' · ') : 'no voice yet'}</div>
                    </div>
                  </div>
                  {p.bio && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-2)' }}>{p.bio}</p>}
                  {draft?.name === p.name && (
                    <div style={{ display: 'grid', gap: 4, padding: 8, border: '1px solid var(--line)', borderRadius: 8 }} data-tool="talent-bio-draft">
                      <span className="sub-dim" style={{ fontSize: 12 }}>The director would say:</span>
                      <p style={{ margin: 0, fontSize: 12.5 }}>{draft.bio}</p>
                      <div className="row" style={{ gap: 4 }}><button type="button" className="btn tiny pri" disabled={busy === 'bio'} onClick={() => void keepBio()}>Keep it</button><button type="button" className="btn tiny" onClick={() => setDraft(null)}>Not this</button></div>
                    </div>
                  )}
                  <div className="row wrap" style={{ gap: 4, alignItems: 'center' }}>
                    <span className="sub-dim" style={{ fontSize: 12 }}>on</span>
                    {p.shows.map((s) => <button key={s.id} type="button" className="btn tiny" onClick={() => onOpenKit(s.id)} title="Open the show's kit to edit">{s.name}</button>)}
                    <span style={{ flex: 1 }} />
                    <button type="button" className="btn tiny" disabled={drafting === p.name} onClick={() => void draftBio(p)} title="The director drafts who they are from their own lines on the show">{drafting === p.name ? 'Drafting…' : p.bio ? 'Redraft the bio' : 'Draft a bio'}</button>
                    <button type="button" className={`btn tiny${isDefault ? ' pri' : ''}`} disabled={busy === 'default'} onClick={() => void setDefault(isDefault ? null : p.name)}
                            title={isDefault ? 'Reads a script that names nobody. Click to unset.' : 'Make this the talent who reads a script that names nobody'}>{isDefault ? 'Reads by default' : 'Read by default'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Face({ src, name, size = 56 }: { src?: string; name: string; size?: number }) {
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', background: 'var(--surface-3, #222)', flex: 'none', display: 'grid', placeItems: 'center', fontWeight: 700 }}>
      {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** One person in the vault: the sheet, the portrait, the voice, the bio, and where they are cast. */
function TalentCard({ t, face, faces, busy, groups, brands, images, pictures, stalled, found, onLookAgain, onRun, onChanged, onBrandSaved, onNote }: {
  t: TalentRow; face?: string; busy: string | null; groups: Array<[string, Voice[]]>; brands: Brand[];
  /** The library's finished pictures, offered as portraits that cost nothing. */
  pictures: Array<{ id: string; name: string; brand_id: string | null }>;
  /** Every face fetched so far, so a previous take can be shown as a thumbnail. */
  faces: Record<string, string>;
  /** What is left to spend on pictures this month. */
  images: Allowance | null;
  /** The watch for this portrait ran out; say so and offer another look. */
  stalled: boolean; onLookAgain: () => void;
  /** The creator was just sent here by typing a name that is already taken. */
  found: boolean;
  onRun: (key: string, fn: () => Promise<void>) => Promise<void>; onChanged: () => Promise<void>; onBrandSaved?: (b: Brand) => void; onNote: (n: string | null) => void;
}) {
  const [sheet, setSheet] = useState(t.sheet ?? '');
  const [bio, setBio] = useState(t.bio ?? '');
  const [voice, setVoice] = useState(t.voice_id ?? '');
  const [show, setShow] = useState(brands[0]?.id ?? '');
  const [extra, setExtra] = useState('');
  // 195: whether a portrait is coming is the SERVER's answer, not this page's.
  // It used to be page state, so a reload lost it, the card then looked like
  // one that had never been drawn, and pressing Draw bought the same face a
  // second time. Now the row says so and a reload changes nothing.
  const coming = t.portrait === 'drawing' || t.portrait === 'developing';
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  useEffect(() => {
    // One clock over both halves of the wait. After a reload it counts from
    // the reload, which is honest: what it claims is how long you have been
    // looking at it, not a duration it cannot know.
    if (coming && startedAt === null) setStartedAt(Date.now());
    if (!coming && startedAt !== null) setStartedAt(null);
  }, [coming, startedAt]);
  useEffect(() => { setSheet(t.sheet ?? ''); setBio(t.bio ?? ''); setVoice(t.voice_id ?? ''); }, [t.id, t.sheet, t.bio, t.voice_id]);
  const dirty = sheet.trim() !== (t.sheet ?? '') || bio.trim() !== (t.bio ?? '') || voice !== (t.voice_id ?? '');
  const on = brands.filter((b) => b.voices?.[t.name] || b.portraits?.[t.name] || b.bios?.[t.name]);
  const k = (s: string) => `${s}:${t.id}`;
  // What a draw will cost out of this month's allowance, said before the press.
  const left = images && images.limit !== null ? Math.max(0, images.limit - images.used) : null;
  const spent = left === 0 && !(images?.soft ?? false);
  // Draw used to test the SAVED sheet while the box beside it held what the
  // creator had just typed. So typing a description and pressing Draw did
  // nothing at all, and the reason sat in a line most people never read —
  // the same silent refusal that left three characters faceless. A sheet in
  // the box counts, and Draw saves it on the way through.
  const sheetNow = sheet.trim();
  const sheetUnsaved = sheetNow.length > 0 && sheetNow !== (t.sheet ?? '');
  return (
    <div className="card" data-talent={t.id} style={{ display: 'grid', gap: 8, padding: 10, borderColor: found ? 'var(--accent, #e0b100)' : undefined }} data-tool="talent-row">
      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
        <Face src={face} name={t.name} size={64} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong style={{ fontSize: 14 }}>{t.name}</strong>
          <div className="sub-dim" style={{ fontSize: 12 }}>
            {coming
              ? sayWait(t.portrait === 'drawing', waited)
              : on.length ? `on ${on.map((b) => b.name).join(', ')}` : 'not cast on a show yet'}
          </div>
        </div>
        <button type="button" className="btn tiny" disabled={Boolean(busy)} title="Remove from the vault (the shows keep what was copied onto them)"
                onClick={() => { if (!window.confirm(`Remove ${t.name} from the vault?`)) return; void onRun(k('rm'), async () => { await api.removeTalent(t.id); await onChanged(); }); }}>×</button>
      </div>
      <label className="field">
        <span className="sub-dim">Looks like</span>
        <textarea value={sheet} maxLength={600} rows={2} placeholder="anime boy, twelve, short black hair, blue shirt, grey shorts" onChange={(e) => setSheet(e.target.value)} />
      </label>
      <div className="row wrap" style={{ gap: 6, alignItems: 'center' }}>
        <input value={extra} maxLength={300} placeholder="Anything more for the portrait — smiling, in profile" onChange={(e) => setExtra(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
        <button type="button" className="btn tiny pri" disabled={Boolean(busy) || !sheetNow || coming || spent}
                title={!sheetNow ? 'Say what they look like first — the portrait is drawn from that'
                  : spent ? 'No pictures left this month'
                  : coming ? 'One is already coming — a second would be bought, not free'
                  : `${t.portrait_asset_id ? 'Redraw the portrait — keeps the face, follows the sheet. It buys another picture.' : 'Draw the portrait from the sheet'}. Takes ${PICTURE_WAIT}.`}
                onClick={() => void onRun(k('draw'), async () => {
                  // Saved on the way through. Pressing Draw is unambiguous
                  // about intent, and refusing it over an unpressed Save is
                  // the software being right at the creator's expense.
                  if (sheetUnsaved) await api.saveTalent(t.id, { sheet: sheetNow });
                  await api.drawPortrait(t.id, { prompt: extra.trim() || undefined, brandId: show || null });
                  setExtra(''); onNote(null); await onChanged();
                })}>
          {coming ? sayWait(t.portrait === 'drawing', waited) : t.portrait_asset_id ? 'Redraw the portrait' : 'Draw the portrait'}
        </button>
      </div>
      {coming && stalled
        ? <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="talent-stalled">
            {GAVE_UP} <button type="button" className="btn tiny" onClick={onLookAgain}>Look again</button>
          </p>
        : coming
        ? <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="talent-waiting">
            {t.portrait === 'drawing'
              ? `Drawing ${t.name} from the sheet. About twenty seconds.`
              : `Drawn. The render plane is looking at the file now, the way it does an upload — a minute or two, and longer when it starts cold. ${t.name}'s face appears here when it is done; you do not need to reload, and pressing Draw again would buy a second picture, so it is held shut.`}
          </p>
        : t.portrait === 'error'
        ? <p className="err inline" style={{ margin: 0, fontSize: 12 }}>That picture failed to process. Drawing again buys another.</p>
        // The button is shut until there is a sheet, and the reason used to
        // live only in a tooltip nobody hovers.
        : !sheetNow
        ? <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="talent-needs-sheet">Say what {t.name} looks like above — the portrait is drawn from that, and so is every scene they are in.</p>
        : sheetUnsaved
        ? <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="talent-will-save">Draw saves what you have typed, then draws from it. Takes {PICTURE_WAIT}.</p>
        : <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="talent-quote">
            Takes {PICTURE_WAIT}. {left === null ? 'Uses one of your pictures.' : left > 0 ? `Uses one of the ${left} pictures you have left this month.` : 'No pictures left this month.'}
          </p>}

      {/* The library first. A picture already in the library — a concept
          sheet, an upload, an earlier generation — becomes the portrait with
          one pick and no spend. Draw is for the face the library has not got. */}
      {pictures.length > 0 && !coming && (
        <label className="field" data-tool="talent-from-library">
          <span className="sub-dim">Or use a picture from the library</span>
          <select value="" disabled={Boolean(busy)} aria-label={`A library picture for ${t.name}`}
                  onChange={(e) => {
                    const id = e.target.value; if (!id) return;
                    void onRun(k('pick'), async () => { await api.saveTalent(t.id, { portraitAssetId: id }); onNote(`${t.name}'s portrait is now ${pictures.find((p) => p.id === id)?.name ?? 'that picture'}. Nothing was bought.`); await onChanged(); });
                  }}>
            <option value="">{t.portrait_asset_id ? 'Swap the portrait for a library picture…' : 'Pick a portrait from the library…'}</option>
            {pictures.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}

      {/* 195: the takes a redraw replaced. Bought already, so switching back
          costs nothing — without this, preferring the earlier face means
          paying for it twice. */}
      {t.portrait_takes.length > 0 && (
        <div className="row wrap" style={{ gap: 6, alignItems: 'center' }} data-tool="talent-takes">
          <span className="sub-dim" style={{ fontSize: 12 }}>Earlier takes:</span>
          {t.portrait_takes.map((id) => (
            <button key={id} type="button" className="btn tiny" disabled={Boolean(busy) || coming}
                    title="Use this one instead — it is already paid for, so this costs nothing"
                    onClick={() => void onRun(k(`take-${id}`), async () => { await api.restorePortrait(t.id, id); await onChanged(); })}>
              {faces[id]
                ? <img src={faces[id]} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', verticalAlign: 'middle' }} />
                : 'take'}
            </button>
          ))}
        </div>
      )}
      <label className="field">
        <span className="sub-dim">Voice</span>
        <select value={voice} onChange={(e) => setVoice(e.target.value)}>
          <option value="">none yet</option>
          {groups.map(([g, vs]) => <optgroup key={g} label={g}>{vs.map((v) => <option key={v.id} value={v.id}>{v.label}{v.hint ? ` — ${v.hint}` : ''}</option>)}</optgroup>)}
        </select>
      </label>
      <label className="field">
        <span className="sub-dim">Who they are</span>
        <textarea value={bio} maxLength={600} rows={2} placeholder="The script writer is told, so they open as themselves." onChange={(e) => setBio(e.target.value)} />
      </label>
      <div className="row wrap" style={{ gap: 6, alignItems: 'center' }}>
        <button type="button" className={`btn tiny${dirty ? ' pri' : ''}`} disabled={Boolean(busy) || !dirty}
                onClick={() => void onRun(k('save'), async () => { await api.saveTalent(t.id, { sheet: sheet.trim() || null, bio: bio.trim() || null, voiceId: voice || null }); await onChanged(); })}>
          {busy === k('save') ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </button>
        <span style={{ flex: 1 }} />
        {brands.length > 0 && (
          <>
            <select value={show} onChange={(e) => setShow(e.target.value)} title="Which show to cast on">
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button type="button" className="btn tiny" disabled={Boolean(busy) || !show || dirty || coming}
                    title={dirty ? 'Save first' : coming ? 'Wait for the face — casting now would cast them without one' : 'Copy the voice, face and bio onto this show\'s kit'}
                    onClick={() => void onRun(k('cast'), async () => {
                      const r = await api.castTalent(t.id, show);
                      onBrandSaved?.(r.brand);
                      onNote(`${t.name} is cast on ${brands.find((b) => b.id === show)?.name ?? 'the show'}${r.cast.face ? '' : ' — without a face yet; draw the portrait and cast again once it is ready'}.`);
                    })}>
              {busy === k('cast') ? 'Casting…' : 'Cast on the show'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export interface Person { name: string; voices: string[]; portraitId: string | null; bio: string | null; shows: { id: string; name: string }[] }

/** The talent across every show, one entry per name; a name on two shows is one person twice cast. */
export function gather(brands: Brand[]): Person[] {
  const by = new Map<string, Person>();
  for (const b of brands) {
    const names = new Set([...Object.keys(b.voices ?? {}), ...Object.keys(b.portraits ?? {}), ...Object.keys(b.bios ?? {})]);
    for (const name of names) {
      const p = by.get(name) ?? { name, voices: [], portraitId: null, bio: null, shows: [] };
      const v = b.voices?.[name]; if (v && !p.voices.includes(v)) p.voices.push(v);
      if (!p.portraitId && b.portraits?.[name]) p.portraitId = b.portraits[name]!;
      if (!p.bio && b.bios?.[name]?.trim()) p.bio = b.bios[name]!.trim();
      p.shows.push({ id: b.id, name: b.name });
      by.set(name, p);
    }
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
}
